import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowLeft } from "lucide-react";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";

const Contact = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const structuredData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "ContactPage",
          "@id": "https://namebiolab.lovable.app/contact#webpage",
          "url": "https://namebiolab.lovable.app/contact",
          "name": "Contact NameBioLab - Get in Touch",
          "description": "Have questions about our AI username and bio generator? Contact the NameBioLab team for support, feedback, or partnership inquiries.",
          "isPartOf": {
            "@id": "https://namebiolab.lovable.app/#webapp"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://namebiolab.lovable.app/contact#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://namebiolab.lovable.app/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Contact",
              "item": "https://namebiolab.lovable.app/contact"
            }
          ]
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name || !email || !message) {
      toast({
        title: "Missing fields",
        description: "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    
    // Simulate form submission
    setTimeout(() => {
      setLoading(false);
      toast({
        title: "Message sent!",
        description: "We'll get back to you soon.",
      });
      setName("");
      setEmail("");
      setMessage("");
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </Link>

        <div className="bg-card rounded-2xl shadow-lg p-6 md:p-8">
          <h1 className="text-4xl font-bold mb-2">Contact Us</h1>
          <p className="text-muted-foreground mb-8">
            Have questions or feedback? We'd love to hear from you!
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your.email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                placeholder="Tell us what's on your mind..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="min-h-[150px]"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-primary to-secondary"
            >
              {loading ? "Sending..." : "Send Message"}
            </Button>
          </form>

          <div className="mt-8 pt-8 border-t border-border">
            <h2 className="text-2xl font-semibold mb-4">Other Ways to Reach Us</h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                <strong className="text-foreground">General Inquiries:</strong><br />
                For general questions about NameBioLab, our AI username and bio generator, or partnership opportunities, 
                please use the contact form above. We typically respond within 24-48 hours during business days.
              </p>
              <p>
                <strong className="text-foreground">Technical Support:</strong><br />
                Experiencing issues with our username generator or bio creator? Describe the problem in detail using 
                the form above, and our technical team will assist you promptly.
              </p>
              <p>
                <strong className="text-foreground">Business Partnerships:</strong><br />
                Interested in advertising opportunities, collaborations, or API access? Reach out through our contact 
                form with "Partnership" in the subject line.
              </p>
              <p>
                <strong className="text-foreground">Press & Media:</strong><br />
                For media inquiries, press releases, or interview requests about NameBioLab and our AI technology, 
                please contact us using the form above.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
